import React, { useState, useEffect, useMemo } from 'react';
import { withStyles } from '@material-ui/core/styles';
import { useSelector } from 'react-redux';
import axios from 'axios';
import Dialog from '@material-ui/core/Dialog';
import MuiDialogTitle from '@material-ui/core/DialogTitle';
import MuiDialogContent from '@material-ui/core/DialogContent';
import { translator } from '../../utils/Translator';
import CloseIcon from '@material-ui/icons/Close';
import {
  Box,
  Typography,
  IconButton,
  Grid,
} from "@material-ui/core";
import Swal from 'sweetalert2';

import MainButton from "../UIElements/MainButton";
import NumberInput from '../FormElements/NumberInput';
import SelectInput from "../FormElements/SelectInput";

const styles = (theme) => ({
  root: {
    margin: 0,
    padding: theme.spacing(2),
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500],
    fontSize: 12
  },
});

const DialogTitle = withStyles(styles)((props) => {
  const { children, classes, onClose } = props;
  return (
    <MuiDialogTitle disableTypography className={classes.root}>
      <Typography variant="h6">{children}</Typography>
      <IconButton aria-label="close" className={classes.closeButton} onClick={onClose}>
        <CloseIcon />
      </IconButton>
    </MuiDialogTitle>
  );
});

const DialogContent = withStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
  },
}))(MuiDialogContent);

const DialogProductDiscountForm = ( props ) => {
  const { open, action, editData = null, product = null, discountLevel, existingDiscounts = [], onCloseDialog, docType = null } = props;

  const { translate } = useSelector(({ i18n }) => i18n);
  const { authUser } = useSelector(({ auth }) => auth);
  const { languageSelected } = useSelector(({ settings }) => settings);

  const [loading, setLoading] = useState(false);

  // ตัวเลือกหน่วยนับ - ถ้ามี product ให้ใช้หน่วยนับจากสินค้า ถ้าไม่มีให้ใช้ค่า default
  const [unitList, setUnitList] = useState([]);
  const [selectedUnit, setSelectedUnit] = useState(null);

  // ตัวเลือกประเภทส่วนลด - ดึงจาก API
  const [discountTypeList, setDiscountTypeList] = useState([]);
  const [availableDiscountTypes, setAvailableDiscountTypes] = useState([]);
  const [selectedDiscountType, setSelectedDiscountType] = useState(null);

  // ตัวเลือกวิธีคำนวณส่วนลด (เปอร์เซ็นต์ หรือ จำนวนเงิน)
  const discountMethodList = useMemo(() => [
    { value: "P", label: translator(translate.ISA_DiscountPercent, "เปอร์เซ็นต์ (%)") },
    { value: "A", label: translator(translate["ISA_Amount(Baht)"], "จำนวนเงิน (บาท)") },
  ], [translate]);
  const [selectedDiscountMethod, setSelectedDiscountMethod] = useState(null);

  // จำนวนส่วนลด
  const [discountAmount, setDiscountAmount] = useState(0);

  // useEffect สำหรับโหลดประเภทส่วนลดจาก API
  useEffect(() => {
    const fetchDiscountTypeList = async () => {
      if (!open) return;

      setLoading(true);

      const params = {
        company: authUser.company !== "#" ? authUser.company : "",
        language: languageSelected.id,
        discountLevel: discountLevel || "L", // L ส่วนลดรายการ, H ส่วนลดท้ายบิล
        ...(docType && { docType: docType }) // เพิ่ม docType ถ้ามี
      }
    
      const url = `${process.env.REACT_APP_API_URL}discountType`;
      const config = {
        params,
        headers: { 
          "Authorization": `Bearer ${localStorage.getItem('x-access-token')}` 
        }
      };
    
      try {
        const response = await axios.get(url, config);
        console.log("📡 Discount Type API Response:", response.data);
        
        const items = response.data;

        // ตรวจสอบว่า response มี data หรือไม่
        if (!items) {
          console.warn(" No data in response");
          setDiscountTypeList([]);
          return;
        }

        // ตรวจสอบว่า items.data เป็น array หรือไม่
        const dataArray = items.data || items || [];
        
        if (!Array.isArray(dataArray)) {
          console.warn(" Response data is not an array:", dataArray);
          setDiscountTypeList([]);
          return;
        }

        // แปลงข้อมูล JSON เป็น { value, label }
        const formattedOptions = dataArray.map((item) => ({
          value: item.DiscountType || item.discountType || item.value,
          label: item.DiscountTypeDescription || item.discountTypeDescription || item.label || item.DiscountType || item.discountType,
        }));

        console.log(" Formatted discount types:", formattedOptions);
        setDiscountTypeList(formattedOptions);

      } catch (error) {
        console.error(' Error fetching discount type list:', error);
        console.error('Error details:', {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status,
          url: url,
          params: params
        });
        setDiscountTypeList([]);
        
        // แสดง error message ถ้าเป็น server error
        if (error.response?.status >= 500) {
          Swal.fire({
            icon: 'error',
            title: translator(translate.ISA_Title_Error, 'เกิดข้อผิดพลาด'),
            text: translator(translate.ISA_ErrorLoadingData, 'ไม่สามารถโหลดข้อมูลประเภทส่วนลดได้ กรุณาลองใหม่อีกครั้ง'),
            confirmButtonText: translator(translate.ISA_OK, 'ตกลง'),
          });
        }
      } finally {
        setLoading(false);
      }
    };

    fetchDiscountTypeList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, authUser, languageSelected]);

  // useEffect สำหรับ filter ประเภทส่วนลดที่ซ้ำ
  useEffect(() => {
    if (discountTypeList.length > 0) {
      // หา discount codes ที่ใช้แล้ว (ไม่รวม discount ที่กำลัง edit)
      const usedDiscountCodes = existingDiscounts
        .filter(discount => action === "edit" ? discount.id !== editData?.id : true)
        .map(discount => discount.discountCode);

      // filter เฉพาะประเภทส่วนลดที่ยังไม่ได้ใช้
      const available = discountTypeList.filter(type => 
        !usedDiscountCodes.includes(type.value)
      );

      setAvailableDiscountTypes(available);

      // ถ้าประเภทส่วนลดที่เลือกไว้ไม่อยู่ในรายการที่พร้อมใช้งาน ให้ reset
      if (selectedDiscountType && !available.some(type => type.value === selectedDiscountType.value)) {
        setSelectedDiscountType(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [discountTypeList, existingDiscounts, action, editData]);

  // useEffect สำหรับโหลดหน่วยนับจาก product
  useEffect(() => {
    if (product && open) {
      // สร้างรายการหน่วยนับจากสินค้า
      const productUnits = [
        { value: product.Unit1, label: product.Unit1 },
        { value: product.Unit2, label: product.Unit2 },
        { value: product.Unit3, label: product.Unit3 },
        { value: product.Unit4, label: product.Unit4 },
      ].filter((u) => u.value && u.value !== "" && u.value !== null && u.value !== "Undefined" && u.value !== "UNDEFINE");

      if (productUnits.length > 0) {
        setUnitList(productUnits);
      } else {
        // ถ้าไม่มีหน่วยนับจากสินค้า ให้ใช้ค่า default
        setUnitList([
          { value: "ชิ้น", label: translator(translate.ISA_UnitPiece, "ชิ้น") },
          { value: "กล่อง", label: translator(translate.ISA_UnitBox, "กล่อง") },
          { value: "ลัง", label: translator(translate.ISA_UnitCrate, "ลัง") },
          { value: "แพ็ค", label: translator(translate.ISA_UnitPack, "แพ็ค") },
        ]);
      }
    } else if (!product && open) {
      // ถ้าไม่มีสินค้า (เช่น ส่วนลดท้ายบิล) ให้ใช้หน่วยนับ default
      setUnitList([
        { value: "ชิ้น", label: translator(translate.ISA_UnitPiece, "ชิ้น") },
        { value: "กล่อง", label: translator(translate.ISA_UnitBox, "กล่อง") },
        { value: "ลัง", label: translator(translate.ISA_UnitCrate, "ลัง") },
        { value: "แพ็ค", label: translator(translate.ISA_UnitPack, "แพ็ค") },
      ]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product, open]);

  // useEffect สำหรับ populate ข้อมูลเมื่ออยู่ใน edit mode
  useEffect(() => {
    if (action === "edit" && editData && open) {
      console.log("📝 Edit mode - Populating discount data:", editData);
      // ตั้งค่าข้อมูลส่วนลด
      setSelectedUnit(editData.unit ? { value: editData.unit, label: editData.unit } : null);
      setSelectedDiscountType(editData.discountCode ? { value: editData.discountCode, label: editData.discountName || editData.discountCode } : null);
      setSelectedDiscountMethod(editData.discountType ? { value: editData.discountType, label: editData.discountType === "P" ? translator(translate.ISA_DiscountPercent, "เปอร์เซ็นต์ (%)") : translator(translate["ISA_Amount(Baht)"], "จำนวนเงิน (บาท)") } : null);
      setDiscountAmount(editData.discountAmount || 0);
      console.log(" Discount data populated:", {
        unit: editData.unit,
        discountCode: editData.discountCode,
        discountType: editData.discountType,
        discountAmount: editData.discountAmount
      });
    } else if (action === "add" && open) {
      // ถ้าเป็น add mode ให้ reset ค่า
      console.log("➕ Add mode - Resetting discount form");
      setSelectedUnit(null);
      setSelectedDiscountType(null);
      setSelectedDiscountMethod(null);
      setDiscountAmount(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [action, editData, open]);

  const handleSubmit = () => {
    console.log("🔥 Discount Form submitted");
    
    // สร้าง array สำหรับเก็บ error messages
    const errors = [];

    // Validation
    if (!selectedUnit) {
      errors.push(`🔸 <strong>${translator(translate.ISA_UnitName, "หน่วยนับ")}:</strong> ${translator(translate.ISA_PleaseSelectUnit, "กรุณาเลือกหน่วยนับ")}`);
    }

    if (availableDiscountTypes.length === 0) {
      errors.push(`🔸 <strong>${translator(translate.ISA_DiscountType, "ประเภทส่วนลด")}:</strong> ${translator(translate.ISA_AllDiscountTypesUsed, "ประเภทส่วนลดทั้งหมดถูกใช้งานแล้ว")}`);
    } else if (!selectedDiscountType) {
      errors.push(`🔸 <strong>${translator(translate.ISA_DiscountType, "ประเภทส่วนลด")}:</strong> ${translator(translate.ISA_PleaseSelectDiscountType, "กรุณาเลือกประเภทส่วนลด")}`);
    }

    if (!selectedDiscountMethod) {
      errors.push(`🔸 <strong>${translator(translate.ISA_DiscountMethodLabel, "วิธีคำนวณ")}:</strong> ${translator(translate.ISA_PleaseSelectDiscountMethod, "กรุณาเลือกวิธีคำนวณส่วนลด")}`);
    }

    if (!discountAmount || discountAmount <= 0) {
      errors.push(`🔸 <strong>${translator(translate.ISA_DiscountAmount, "จำนวนส่วนลด")}:</strong> ${translator(translate.ISA_PleaseEnterDiscountAmount, "กรุณาระบุจำนวนส่วนลด")}`);
    }

    // ถ้ามี error ให้แสดง SweetAlert2
    if (errors.length > 0) {
      Swal.fire({
        icon: 'warning',
        title: translator(translate.ISA_PleaseCheck, "กรุณาตรวจสอบข้อมูล"),
        html: `
          <div style="text-align: left; padding: 10px;">
            <p style="margin-bottom: 10px; color: #666;">
              ${translator(translate.ISA_FillDataBeforeSave, "กรุณากรอกข้อมูลให้ครบถ้วนก่อนบันทึก")}:
            </p>
            <div style="background: #fff3e0; padding: 15px; border-radius: 8px; border-left: 4px solid #ff9800;">
              ${errors.join('<br/>')}
            </div>
          </div>
        `,
        confirmButtonText: translator(translate.ISA_OK, "ตกลง"),
        confirmButtonColor: '#ff9800',
        scrollbarPadding: false,
        heightAuto: false,
      });
      return;
    }

    // สร้าง object ที่จะส่งกลับ
    const discountData = {
      // Unique identifier (ถ้าเป็น edit ให้ใช้ id เดิม, ถ้าเป็น add ให้สร้างใหม่)
      id: action === "edit" && editData ? editData.id : Date.now(),
      
      // ข้อมูลส่วนลด
      unit: selectedUnit.value,
      discountCode: selectedDiscountType.value,
      discountName: selectedDiscountType.label,
      discountType: selectedDiscountMethod.value,
      discountAmount: discountAmount,
      discountLevel: discountLevel, // L ส่วนลดรายการ, H ส่วนลดท้ายบิล
    };

    console.log(" Discount Data to return:", discountData);
    
    // ส่งข้อมูลกลับไปหน้าหลัก
    onCloseDialog(discountData);
  };

  return (
    <Dialog
        fullWidth
        maxWidth="sm"
        open={open}
        onClose={() => {}}
        disableBackdropClick
        disableEscapeKeyDown>
        <DialogTitle
          id="customized-dialog-title"
          onClose={() => onCloseDialog(null)} >
          { action === "add" ? translator(translate.ISA_AddSequence, "เพิ่มส่วนลด") : translator(translate.ISA_EditDiscount, "แก้ไขส่วนลด") }
        </DialogTitle>
        <DialogContent>
            {/* แสดงชื่อสินค้าและรหัสสินค้า (ถ้ามี) */}
            {product && (
              <Box p={2} bgcolor="#f5f5f5" borderRadius={2} mb={2}>
                <Typography variant="subtitle1" style={{ fontWeight: "bold", color: "#333" }}>
                  {product.ProductName}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {translator(translate.ISA_ProductCode, "รหัสสินค้า")}: {product.ProductCode}
                </Typography>
              </Box>
            )}

            <Box p={2}>
              <Typography variant="h6" gutterBottom>
                {translator(translate.ISA_DiscountDetail, "รายละเอียดส่วนลด")}
              </Typography>

              {/* บรรทัดที่ 1: หน่วยนับ และ ประเภทส่วนลด */}
              <Grid container spacing={2} style={{ marginTop: 16 }}>
                <Grid item xs={6}>
                  <SelectInput 
                    label={translator(translate.ISA_UnitName, "หน่วยนับ")}
                    value={selectedUnit}
                    onChange={setSelectedUnit}
                    options={unitList}
                    placeholder={translator(translate.ISA_SelectUnit, "เลือกหน่วยนับ")}
                    isClearable
                    isSearchable />
                </Grid>
                <Grid item xs={6}>
                  <SelectInput 
                    label={translator(translate.ISA_DiscountType, "ประเภทส่วนลด")}
                    value={selectedDiscountType}
                    onChange={setSelectedDiscountType}
                    options={availableDiscountTypes}
                    placeholder={availableDiscountTypes.length === 0 ? translator(translate.ISA_AllDiscountTypesUsed, "ประเภทส่วนลดถูกใช้หมดแล้ว") : translator(translate.ISA_PleaseSelectDiscountType, "เลือกประเภทส่วนลด")}
                    isClearable
                    isSearchable
                    isDisabled={availableDiscountTypes.length === 0} />
                </Grid>
              </Grid>

              {/* บรรทัดที่ 2: วิธีคำนวณส่วนลด และ จำนวนส่วนลด */}
              <Grid container spacing={2} style={{ marginTop: 16 }}>
                <Grid item xs={6}>
                  <SelectInput 
                    label={translator(translate.ISA_DiscountMethodLabel, "วิธีคำนวณส่วนลด")}
                    value={selectedDiscountMethod}
                    onChange={setSelectedDiscountMethod}
                    options={discountMethodList}
                    placeholder={translator(translate.ISA_SelectDiscountMethod, "เลือกวิธีคำนวณ")}
                    isClearable
                    isSearchable />
                </Grid>
                <Grid item xs={6}>
                  <Box>
                    <Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
                      {translator(translate.ISA_DiscountAmount, "จำนวนส่วนลด")} {selectedDiscountMethod?.value === "P" ? "(%)" : selectedDiscountMethod?.value === "A" ? "(บาท)" : ""}
                    </Typography>
                    <NumberInput 
                      value={discountAmount} 
                      textAlign='right'
                      onChange={(val) => setDiscountAmount(parseFloat(val) || 0)} 
                      onFocus={(e) => e.target.select()}
                      decimalScale={2} />
                  </Box>
                </Grid>
              </Grid>
            </Box>

            <hr />

            <div className="row">
              <div className="col-12" style={{ display: "flex", justifyContent: "flex-end", gap: "4px" }}>
                  <MainButton
                    label={translator(translate.ISA_Cancel1, "ยกเลิก")}
                    color={"bg-red"}
                    handleClick={() => onCloseDialog(null)} 
                    disabled={loading} />

                  <MainButton
                    label={translator(translate.ISA_Button_SubmitForm, "บันทึกรายการ")}
                    handleClick={() => handleSubmit()}
                    disabled={loading} />
                    
                </div>
              </div>
          </DialogContent>
      </Dialog>
  )
}

export default DialogProductDiscountForm;